/**
 * The printed plan copy's document body — composed, and testable.
 *
 * OMON-042 / OMON-289 built one long-form document that always carries the
 * whole recording history. Ed, 2026-09-16, relaying a business user: they want
 * to CHOOSE — "plan only" or "plan AND all the narrative" — and only the
 * second exists today.
 *
 * "Plan only" is not simply two fewer headings. The narrative is woven through
 * the plan sections as well: goal charts plot recorded scores as dots, and each
 * monitored event renders a chart of its recording history. So the caller
 * neutralises those at source (passing no actuals, no chart) and this module
 * decides the section-level shape.
 *
 * What plan-only KEEPS, deliberately:
 *   - every goal and its planned trajectory line (Ed, 2026-09-16: the intended
 *     shape of progress is plan content, not narrative);
 *   - every monitored event with its target, mandatory and escalation chips
 *     and its prompt.
 *   - every log (diary-kind trackable) with its prompt, under "What carers
 *     record" and with no entries (Ed, 2026-09-30, reversing 09-16).
 * What it DROPS whole: the Combined narrative, and every recorded entry.
 *
 * PAGINATION (Ed, 2026-09-21). The browser's print engine decides page breaks,
 * steered only by CSS hints (index.html.template, `.print-doc-region`):
 *   - COVER PAGE: the client card prints alone on page 1 and the plan starts
 *     on page 2 (`.pd-plan-start` → break-before: page). The card nearly fills
 *     an A4-landscape page anyway, so this is the layout that is stable across
 *     clients rather than one that depends on how tall this client's card is.
 *   - KEEP-TOGETHER: each section heading travels WITH its first item
 *     (`.pd-keep` → break-inside: avoid). This is the half a previous rule was
 *     missing: `.pd-item` was already unsplittable, so when an item did not fit
 *     the browser moved it whole to the next page — and left its heading
 *     stranded at the bottom of the previous one.
 * Why a wrapper rather than `break-after: avoid` on the heading alone: support
 * for "avoid" between siblings is uneven across engines, whereas break-inside:
 * avoid on a single block is honoured everywhere. The heading keeps the hint
 * too, for the one section that cannot be grouped (the narrative is one blob).
 *
 * Pure string composition: no DOM, no `state`, no Firestore.
 */

export interface PlanCopyParts {
  /** The client tile, already rendered (or a fallback heading). */
  heroHtml: string;
  /** The plan card, already rendered. May be empty. */
  planCardHtml: string;
  /** One rendered `.pd-item` per goal, in order. */
  goalItems: string[];
  /** One rendered `.pd-item` per monitored event, in order. */
  monitoredItems: string[];
  /** One rendered `.pd-item` per log (diary-kind trackable). Plan-only
   *  callers pass items WITHOUT entries. */
  diaryItems: string[];
  /** The combined narrative transcript. Ignored when plan-only. */
  narrativeHtml: string;
}

export const EMPTY = `<p class="pd-sub">None on this plan.</p>`;

/**
 * A section whose heading can never be stranded: the heading and the FIRST
 * item (or the empty-state line) are one unsplittable block; the remaining
 * items follow outside it and paginate freely. Only the first item is grouped
 * — grouping the whole section would push a long section to the next sheet
 * whole and leave the previous one near-blank.
 */
export function keptSection(heading: string, items: string[], empty = EMPTY): string {
  const [first, ...rest] = items;
  return [
    `<div class="pd-keep"><h2 class="pd-h2">${heading}</h2>${first ?? empty}</div>`,
    ...rest,
  ].join("");
}

/**
 * Compose the print region's inner HTML.
 *
 * `includeNarrative: false` drops the narrative sections entirely and expects
 * the caller to have passed narrative-free goal and monitored-event items.
 * `coverPage` starts the plan on a fresh page after the client card; pass it
 * only when the REAL card rendered — after the fallback name heading it would
 * leave page 1 holding nothing but a name.
 */
export function planCopyBody(
  parts: PlanCopyParts,
  opts: { includeNarrative: boolean; coverPage: boolean },
): string {
  const plan = [
    parts.planCardHtml,
    keptSection(`Developmental goals (${parts.goalItems.length})`, parts.goalItems),
    keptSection(`Monitored events (${parts.monitoredItems.length})`, parts.monitoredItems),
  ];
  // The logs (diary-kind trackables: Shift log, ABC chart, sleep pattern,
  // Plan for the Day) ARE plan content: things we have committed to do for
  // the client. Ed 2026-09-16 dropped them from plan-only because a section
  // headed "Shift log" reads as narrative; Ed 2026-09-30 (Rebekah: a plan
  // outline for GPs and social workers) reversed that, since dropping them
  // hides parts of the plan. So plan-only lists them, title + prompt and NO
  // entries, under a heading that reads as the plan, not as a record.
  if (opts.includeNarrative) {
    plan.push(`<h2 class="pd-h2">Combined narrative</h2>`, parts.narrativeHtml);
    if (parts.diaryItems.length > 0) {
      plan.push(keptSection("Shift log", parts.diaryItems));
    }
  } else if (parts.diaryItems.length > 0) {
    plan.push(keptSection(`What carers record (${parts.diaryItems.length})`, parts.diaryItems));
  }
  const planClass = opts.coverPage ? "pd-plan pd-plan-start" : "pd-plan";
  return [
    parts.heroHtml,
    `<div class="${planClass}">`,
    ...plan.filter((s) => s !== ""),
    `</div>`,
  ].join("\n    ");
}

/**
 * The document title, which is also the suggested "Save as PDF" filename.
 * Plan-only says so on the file, because two PDFs of the same plan on the
 * same day are otherwise indistinguishable in a downloads folder.
 */
export function planCopyTitle(
  clientName: string,
  dateStamp: string,
  includeNarrative: boolean,
): string {
  const kind = includeNarrative ? "SOMA Plan" : "SOMA Plan (plan only)";
  return `${clientName} — ${kind} — ${dateStamp}`;
}
